(() => {
  'use strict';

  if (window.WellHomeProductQuality) return;
  window.WellHomeProductQuality = true;

  const mobileProduct = window.matchMedia('(max-width: 989px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const readProductData = () => {
    const source = document.querySelector('[data-wh-product-variants]');
    if (!source) return null;

    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      console.warn('WellHome: no se han podido leer los datos de variantes.', error);
      return null;
    }
  };

  const updateVariantUrl = (productUrl, variantId) => {
    if (!variantId || !window.history?.replaceState) return;

    const url = new URL(productUrl || window.location.href, window.location.origin);
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.forEach((value, key) => {
      if (key !== 'variant') url.searchParams.set(key, value);
    });
    url.searchParams.set('variant', variantId);
    window.history.replaceState({ variantId }, '', url.toString());
  };

  const ensureSku = (productRoot) => {
    let sku = productRoot.querySelector('[data-variant-sku]');
    if (sku) return sku;

    sku = document.createElement('p');
    sku.className = 'whp-sku';
    sku.dataset.variantSku = '';
    sku.setAttribute('aria-live', 'polite');
    sku.hidden = true;

    const anchor = productRoot.querySelector('.whp-tax')
      || productRoot.querySelector('[data-compare-price]')
      || productRoot.querySelector('[data-price]');
    anchor?.insertAdjacentElement('afterend', sku);
    return sku;
  };

  /*
    Anuncia el titulo nuevo a los lectores de pantalla. La region la pinta Liquid vacia en
    el primer render (`[data-wh-variant-status]`, `role="status"`), asi que ya esta
    registrada cuando llega el primer cambio: una region viva creada al mismo tiempo que su
    contenido no se anuncia.

    El texto de delante ("Has elegido:") es un ajuste de seccion, como el resto de las
    cadenas de esta ficha, y viaja en un `data-` para no meter castellano en el JS.
  */
  const announceVariant = (productRoot, title) => {
    const status = productRoot.querySelector('[data-wh-variant-status]');
    if (!status) return;
    const label = (status.dataset.whAnnounceLabel || '').trim();
    status.textContent = label ? `${label} ${title}` : title;
  };

  // Altura del header SOLO si esta pegado arriba tapando contenido. Si ya se ha ido
  // con el scroll no descuenta nada, o la foto quedaria con un hueco de mas.
  const stickyHeaderHeight = () => {
    const header = document.querySelector('#header') || document.querySelector('page-header');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    return rect.top <= 1 ? rect.height : 0;
  };

  /*
    En escritorio la columna de datos es `position: sticky`, asi que el selector de
    variante sigue en pantalla mientras la galeria se va hacia arriba. Al cambiar de
    variante la foto SI se sustituye, pero en el hueco grande, que para entonces esta
    fuera de la vista: da la sensacion de que la seleccion no se ha aplicado.

    Tres cautelas, porque un scroll automatico mal hecho molesta mas que el problema:
    - Si mas de la mitad del hueco ya se ve, no se toca el scroll. El usuario esta
      viendo el cambio y moverle la pagina seria peor que no hacer nada.
    - Se descuenta el header pegajoso, o la foto acaba metida debajo de la cabecera.
    - Si el destino esta a mas de dos pantallas, se salta sin animar. En fichas con
      muchas fotos la galeria pasa de 25.000px y un `smooth` desde el final seria un
      viaje de varios segundos. Igual con `prefers-reduced-motion`.
  */
  const revealVariantMedia = (media) => {
    const rect = media.getBoundingClientRect();
    const offset = stickyHeaderHeight() + 16;

    const visible = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, offset));
    if (rect.height && visible >= rect.height * 0.5) return;

    const target = Math.max(0, window.scrollY + rect.top - offset);
    const isFar = Math.abs(target - window.scrollY) > window.innerHeight * 2;
    window.scrollTo({
      top: target,
      behavior: (isFar || reduceMotion.matches) ? 'auto' : 'smooth'
    });
  };

  const moveToVariantMedia = (productRoot, featuredMediaId, interactive) => {
    if (!featuredMediaId) return;

    const items = Array.from(productRoot.querySelectorAll('[data-gallery-item][data-media-id]'));
    const media = items.find((item) => String(item.dataset.mediaId) === String(featuredMediaId));
    if (!media) return;

    items.forEach((item) => item.classList.toggle('is-variant-media', item === media));

    if (!mobileProduct.matches) {
      // En la carga inicial no se mueve la pagina: el usuario no ha elegido nada todavia.
      if (interactive) revealVariantMedia(media);
      return;
    }

    const gallery = productRoot.querySelector('[data-gallery]');
    if (!gallery) return;

    // En movil el desplazamiento es INTERNO del carrusel, no de la pagina, asi que
    // tambien se hace en la carga inicial: coloca el slide correcto de entrada.
    gallery.scrollTo({ left: media.offsetLeft, behavior: 'smooth' });
    window.setTimeout(() => gallery.dispatchEvent(new Event('scroll')), 350);
  };

  // `interactive` distingue el cambio hecho POR EL USUARIO de la llamada inicial que
  // solo sincroniza el estado al cargar. Gobierna las dos cosas que no deben pasar en
  // la carga: reescribir la URL y mover el scroll de la pagina.
  const applyVariant = (productRoot, productData, variantId, interactive = true) => {
    const variant = productData.variants?.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    const variantInput = productRoot.querySelector('[data-variant-select], .whp-form input[name="id"]');
    if (variantInput && String(variantInput.value) !== String(variant.id)) {
      variantInput.value = String(variant.id);
    }

    const price = productRoot.querySelector('[data-price]');
    if (price) {
      price.textContent = variant.price || '';
      price.setAttribute('aria-live', 'polite');
    }

    const compare = productRoot.querySelector('[data-compare-price]');
    if (compare) {
      const compareValue = variant.comparePrice || '';
      const compareText = compare.querySelector('s');
      if (compareText) compareText.textContent = compareValue;
      compare.hidden = !compareValue;
    }

    const sku = ensureSku(productRoot);
    if (sku) {
      sku.textContent = variant.sku ? `SKU: ${variant.sku}` : '';
      sku.hidden = !variant.sku;
    }

    /*
      TITULO ESPECIFICO. El <h1> pasa del titulo de familia -el que se ve en el listado de
      categoria- al titulo completo de la fila de origen de la variante elegida, con su
      color y sus medidas. Va por el mismo camino que el precio y el SKU: mismo
      `applyVariant`, mismos datos de `[data-wh-product-variants]`, ninguna via paralela.

      Tres cautelas:
      - `textContent`, nunca `innerHTML`. El titulo es contenido de producto migrado, o sea
        dato no confiable (CLAUDE.md regla 6), y aqui se pinta como texto y punto.
      - Si la variante no trae dato se vuelve al titulo de familia que Liquid dejo en
        `data-wh-family-title`, y si tampoco lo hubiera, no se toca el <h1>. El titular no
        se queda nunca en blanco ni con "null".
      - Solo se escribe si el texto CAMBIA. Eso ahorra el anuncio espurio del lector de
        pantalla en la carga inicial, donde el servidor ya pinto el titulo correcto.
    */
    const titleEl = productRoot.querySelector('[data-wh-product-title]');
    if (titleEl) {
      const familyTitle = (titleEl.dataset.whFamilyTitle || productData.productTitle || '').trim();
      const nextTitle = (variant.displayTitle || '').trim() || familyTitle;
      if (nextTitle && nextTitle !== titleEl.textContent.trim()) {
        titleEl.textContent = nextTitle;
        if (interactive) announceVariant(productRoot, nextTitle);
      }
    }

    /*
      TEXTO DE VARIANTE. Este elemento solo existe si el producto no tiene descripcion y alguna
      variante trae `custom.descripcion_variante`; en el resto de las fichas no se hace nada.
      Es dato no confiable (CLAUDE.md regla 6) y texto plano: `textContent`, nunca `innerHTML`.
      Los saltos de linea los respeta el `white-space: pre-line` de `.whp-plain-text`.
      Se oculta el acordeon si la variante no trae texto, y tambien el contenedor si no le
      queda ningun otro acordeon, para que su borde superior no quede como una linea suelta.
    */
    const variantDescription = productRoot.querySelector('[data-wh-variant-description]');
    const descriptionBlock = variantDescription?.closest('details');
    if (descriptionBlock) {
      const nextDescription = (variant.descriptionText || '').trim();
      if (variantDescription.textContent !== nextDescription) {
        variantDescription.textContent = nextDescription;
      }
      descriptionBlock.hidden = !nextDescription;
      const accordions = descriptionBlock.parentElement;
      accordions.hidden = !accordions.querySelector('details:not([hidden])');
    }

    const button = productRoot.querySelector('[data-add-button]');
    if (button) {
      button.disabled = !variant.available;
      const label = variant.available ? button.dataset.addLabel : button.dataset.soldOutLabel;
      if (label) button.textContent = label;
    }

    moveToVariantMedia(productRoot, variant.featuredMediaId, interactive);
    if (interactive) updateVariantUrl(productData.productUrl, variant.id);

    productRoot.dispatchEvent(new CustomEvent('wellhome:variant-change', {
      bubbles: true,
      detail: { variant }
    }));
  };

  const initProduct = (productRoot, productData) => {
    if (productRoot.dataset.whProductQualityReady === 'true') return;
    productRoot.dataset.whProductQualityReady = 'true';

    const variantInput = productRoot.querySelector('[data-variant-select], .whp-form input[name="id"]');
    const initialVariantId = variantInput?.value || productData.selectedVariantId;
    if (!initialVariantId) return;

    variantInput?.addEventListener('change', () => {
      applyVariant(productRoot, productData, variantInput.value, true);
    });

    applyVariant(productRoot, productData, initialVariantId, false);
  };

  /*
    Barra del carrusel de relacionados. Es indicador Y control: no hay flechas, asi que
    esta barra es la unica forma de avanzar con raton. Con teclado no hace falta, porque
    `.whpl-track` lleva `tabindex="0"` y el navegador ya desplaza con las flechas.

    El pulgar mide, en porcentaje del carril, lo mismo que la parte visible mide sobre el
    ancho total: con 8 tarjetas y 4 a la vista sale al 50 %, que es justo lo que dibujo
    Raquel en el diseno. El minimo del 16 % evita que en un carrusel muy largo el pulgar
    quede tan fino que no se pueda coger.
  */
  const initRelatedProgress = (root = document) => {
    root.querySelectorAll('.whpl').forEach((section) => {
      if (section.dataset.whProgressReady === 'true') return;
      const track = section.querySelector('.whpl-track');
      const progress = section.querySelector('.whpl-progress');
      const thumbEl = progress?.querySelector('span');
      if (!track || !progress || !thumbEl) return;
      section.dataset.whProgressReady = 'true';

      let frame = null;
      let thumbRatio = 1;

      const update = () => {
        frame = null;
        const travel = Math.max(0, track.scrollWidth - track.clientWidth);
        const thumb = track.scrollWidth
          ? Math.max(16, Math.min(100, (track.clientWidth / track.scrollWidth) * 100))
          : 100;
        thumbRatio = thumb / 100;
        const available = 100 - thumb;
        const position = travel ? (track.scrollLeft / travel) * available : 0;
        thumbEl.style.width = `${thumb}%`;
        // El translate va en % del ANCHO DEL PULGAR, no del carril, asi que hay que
        // reescalar: mover un 10 % del carril con un pulgar del 50 % son 20 % de si mismo.
        thumbEl.style.transform = `translateX(${position / Math.max(thumb, 1) * 100}%)`;
      };
      const requestUpdate = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };

      // Arrastre y pulsacion. La posicion se toma respecto al CENTRO del pulgar: si se
      // midiera contra el borde, al soltar en un extremo el carrusel se quedaria siempre
      // corto por la mitad del ancho del pulgar.
      const scrollToPointer = (event) => {
        const travel = Math.max(0, track.scrollWidth - track.clientWidth);
        if (!travel) return;
        const rect = progress.getBoundingClientRect();
        const thumbWidth = rect.width * thumbRatio;
        const usable = Math.max(1, rect.width - thumbWidth);
        const offset = event.clientX - rect.left - (thumbWidth / 2);
        const ratio = Math.min(1, Math.max(0, offset / usable));
        track.scrollLeft = ratio * travel;
      };

      // La clase va en los DOS: en la barra cambia el cursor, y en el carril apaga el
      // imantado y la animacion suave, que peleaban con cada `scrollLeft` del arrastre
      // y hacian que el carrusel avanzase a tirones en vez de seguir al raton.
      const startDrag = (event) => {
        const travel = track.scrollWidth - track.clientWidth;
        if (travel <= 0) return;
        progress.setPointerCapture(event.pointerId);
        progress.classList.add('is-dragging');
        track.classList.add('is-dragging');
        scrollToPointer(event);
        event.preventDefault();
      };

      const endDrag = (event) => {
        if (!progress.hasPointerCapture(event.pointerId)) return;
        progress.releasePointerCapture(event.pointerId);
        progress.classList.remove('is-dragging');
        // Al devolver el snap, el navegador reajusta a la tarjeta mas cercana: el
        // carrusel cuadra solo al soltar, sin que el usuario tenga que afinar.
        track.classList.remove('is-dragging');
      };

      progress.addEventListener('pointerdown', startDrag);
      progress.addEventListener('pointermove', (event) => {
        if (!progress.hasPointerCapture(event.pointerId)) return;
        scrollToPointer(event);
      });
      progress.addEventListener('pointerup', endDrag);
      progress.addEventListener('pointercancel', endDrag);

      track.addEventListener('scroll', requestUpdate, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(requestUpdate).observe(track);
      else window.addEventListener('resize', requestUpdate, { passive: true });
      update();
    });
  };

  const initReviewAppFallbacks = (root = document) => {
    root.querySelectorAll('.whpr').forEach((section) => {
      const appBlock = section.querySelector('.whpr-app');
      if (!appBlock) return;
      section.querySelectorAll('.whpr-card').forEach((card) => {
        card.hidden = true;
        card.setAttribute('aria-hidden', 'true');
      });
    });
  };

  const init = (root = document) => {
    const productData = readProductData();
    if (productData) {
      root.querySelectorAll('wellhome-product').forEach((productRoot) => initProduct(productRoot, productData));
    }
    initRelatedProgress(root);
    initReviewAppFallbacks(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
